/**
 * StorageHealthTab — one-glance R2 storage drift tile for admins.
 *
 * Surfaces:
 *  - Latest GC run (purged count, bytes freed, duration)
 *  - 7-day rollup so a falling-behind scheduler is visible
 *  - Pending soft-deleted rows past the 30-day cutoff
 *  - Live R2 bucket total size + object count
 *
 * Anything > 500 pending rows flips the tile from green → yellow so
 * the on-call admin notices BEFORE storage cost shows up on a bill.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  HardDrive, Loader2, CheckCircle2, AlertTriangle, RefreshCw, Clock, Trash2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  healthy:  { Icon: CheckCircle2,   color: '#34C759', label: 'Healthy' },
  warning:  { Icon: AlertTriangle,  color: '#FF9F0A', label: 'Heads up' },
  critical: { Icon: AlertTriangle,  color: '#FF453A', label: 'Critical' },
};

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-[var(--isp-glass-border)] bg-white/[0.02] p-4">
      <p className="text-[11px] uppercase tracking-wider text-white/40 font-medium mb-1.5">{label}</p>
      <p className="text-2xl font-semibold text-white leading-none mb-1">{value}</p>
      {sub && <p className="text-xs text-white/50">{sub}</p>}
    </div>
  );
}

export default function StorageHealthTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/storage-healthcheck`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load storage health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  if (!data && loading) {
    return (
      <div className="flex items-center gap-3 text-white/60 p-8">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading storage health…
      </div>
    );
  }
  if (!data) return null;

  const meta = STATUS_META[data.status] || STATUS_META.warning;
  const { Icon } = meta;
  const latest = data.latest_run;
  const lastRanH = latest?.ran_at
    ? new Date(latest.ran_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : 'Never';
  const purgedLatest = latest
    ? ((latest.uploaded_files?.purged || 0) + (latest.audio_files?.purged || 0))
    : 0;

  return (
    <div className="space-y-5" data-testid="storage-health-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#002FA7]/15 flex items-center justify-center">
            <HardDrive className="w-5 h-5 text-[#7E9EFF]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">R2 Storage Health</h2>
            <p className="text-xs text-white/55">Cloudflare R2 bucket + GC scheduler status</p>
          </div>
        </div>
        <button
          onClick={fetch}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[var(--isp-glass-border)] text-xs text-white/80 hover:bg-white/5 transition disabled:opacity-50"
          data-testid="storage-health-refresh"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Verdict pill */}
      <div
        className="flex items-center gap-3 rounded-xl border px-4 py-3"
        style={{ borderColor: `${meta.color}40`, backgroundColor: `${meta.color}14` }}
        data-testid="storage-health-verdict"
      >
        <Icon className="w-5 h-5 flex-shrink-0" style={{ color: meta.color }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</p>
          <p className="text-xs text-white/65">{data.message}</p>
        </div>
      </div>

      {/* Top-level metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat
          label="Latest GC run"
          value={purgedLatest}
          sub={`purged · ${lastRanH}`}
        />
        <Stat
          label="7-day rollup"
          value={data.week_summary.total_purged}
          sub={`${data.week_summary.runs} run${data.week_summary.runs === 1 ? '' : 's'} · ${data.week_summary.total_bytes_freed_h}`}
        />
        <Stat
          label="Pending cleanup"
          value={data.pending_cleanup.uploaded_files + data.pending_cleanup.audio_files}
          sub={`${data.pending_cleanup.uploaded_files} docs · ${data.pending_cleanup.audio_files} audio`}
        />
        <Stat
          label="Bucket total"
          value={data.bucket?.total_bytes_h ?? '—'}
          sub={data.bucket?.total_objects != null ? `${data.bucket.total_objects} objects` : 'not configured'}
        />
      </div>

      {/* Latest run details */}
      {latest && (
        <div className="rounded-xl border border-[var(--isp-glass-border)] bg-white/[0.02] p-4" data-testid="storage-health-latest">
          <div className="flex items-center gap-2 mb-3">
            <Trash2 className="w-4 h-4 text-white/55" />
            <h3 className="text-sm font-semibold text-white">Latest sweep</h3>
            <span className="text-xs text-white/40 inline-flex items-center gap-1 ml-auto">
              <Clock className="w-3 h-3" /> {latest.duration_ms} ms
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs">
            {['uploaded_files', 'audio_files'].map((k) => {
              const s = latest[k] || {};
              return (
                <div key={k} className="rounded-lg bg-black/20 p-3 border border-white/5">
                  <p className="text-[10px] uppercase tracking-wider text-white/40 font-medium mb-1.5">
                    {k.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-white">
                    <strong>{s.purged || 0}</strong> purged
                    {s.skipped_shared_bytes ? ` · ${s.skipped_shared_bytes} shared-skipped` : ''}
                  </p>
                  <p className="text-xs text-white/50 mt-0.5">
                    from {s.candidates || 0} candidate{s.candidates === 1 ? '' : 's'}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
